<template>
  <q-banner
    v-bind="$attrs"
    :class="bannerClass"
    class="c-banner"
    inline-actions
  >
    <template #avatar>
      <q-icon :name="icon" size="16px" />
    </template>

    <template v-for="(_, slot) in $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>

    <template v-if="props.closable" #action>
      <q-btn
        flat
        dense
        round
        icon="fa-solid fa-xmark"
        size="sm"
        class="c-banner__close"
        @click="emit('close')"
      />
    </template>
  </q-banner>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { bannerIcons } from "common/defaultIcons";
import type { BannerType } from "shared/types";

const props = withDefaults(
  defineProps<{
    type?: BannerType;
    closable?: boolean;
  }>(),
  {
    type: "info",
    closable: false,
  },
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

const icon = computed(() => bannerIcons[props.type]);

const bannerClass = computed(() => `c-banner--${props.type}`);
</script>

<style lang="scss">
$banner-colors: (
  "info": --color-info,
  "warning": --color-warning,
  "negative": --color-negative,
  "positive": --color-positive,
);

div.c-banner {
  @apply tw-min-h-[40px] tw-items-start tw-rounded-lg;

  .q-banner__avatar {
    @apply tw-flex tw-min-h-[24px] tw-items-center;
  }

  .q-banner__content {
    @apply tw-leading-6;
  }

  .q-banner__actions {
    @apply tw-flex tw-min-h-[24px] tw-items-center tw-self-start;
  }

  .c-banner__close {
    @apply tw-h-6 tw-w-6;
  }
}

@each $name, $color in $banner-colors {
  .c-banner--#{$name} {
    background: color-mix(in srgb, rgb(var(#{$color})) 10%, white);
    border: solid 1px color-mix(in srgb, rgb(var(#{$color})) 20%, white);

    .q-banner__avatar .q-icon {
      color: rgb(var(#{$color}));
    }
  }
}
</style>
